/* eslint-disable import/no-deprecated */
import { withinLimits } from '@fullstory/util/date';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Popover } from '../../components/Popover';
import { TextInput } from '../../components/TextInput';
import { DeprecatedDatepicker } from './Datepicker';

export interface TextDatepickerProps
  extends Pick<
      React.ComponentProps<typeof TextInput>,
      'variant' | 'placeholder' | 'size' | 'className' | 'disabled' | 'invalid' | 'onKeyDown'
    >,
    Pick<
      React.ComponentProps<typeof DeprecatedDatepicker>,
      'selectedDate' | 'onChange' | 'endOfDay'
    > {
  onHideDatepicker?: () => void;
  minDate?: Date;
  maxDate?: Date;
}

/**
 * @deprecated in favor of the `DateRangePicker` component.
 */
export const DeprecatedTextDatepicker: React.FC<TextDatepickerProps> = ({
  minDate,
  maxDate,
  onHideDatepicker,
  selectedDate,
  onChange,
  endOfDay,
  ...props
}) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [pendingDate, setPendingDate] = useState<string>(
    selectedDate
      ? intl.formatDate(selectedDate, { year: 'numeric', month: 'numeric', day: 'numeric' })
      : '',
  );
  const [invalid, setInvalid] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const hideDatepicker = useCallback(() => {
    setOpen(false);
    onHideDatepicker && onHideDatepicker();
  }, [onHideDatepicker]);

  // if selectedDate is provided, we always want to reset the pendingDate
  useEffect(() => {
    selectedDate &&
      setPendingDate(
        intl.formatDate(selectedDate, { year: 'numeric', month: 'numeric', day: 'numeric' }),
      );
    setInvalid(false);
  }, [intl, selectedDate]);

  const isValid = useCallback(
    (text: string) => {
      const d = Date.parse(text);
      if (!text || isNaN(Date.parse(text))) return false;
      return withinLimits(new Date(d), minDate, maxDate);
    },
    [minDate, maxDate],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key == 'Enter' && !invalid) {
        inputRef.current && inputRef.current.blur();
        hideDatepicker();
      }
    },
    [invalid, hideDatepicker],
  );

  const onChangeWrapper = useCallback(
    (d: Date) => {
      setInvalid(false);
      setPendingDate(d.toLocaleDateString('en-US'));
      onChange(d);
    },
    [onChange],
  );

  const onTextInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPendingDate(e.target.value);
      if (isValid(e.target.value)) {
        onChangeWrapper(new Date(Date.parse(e.target.value)));
      } else {
        setInvalid(true);
      }
    },
    [isValid, onChangeWrapper],
  );

  return (
    <>
      <TextInput
        type="text"
        ref={inputRef}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        value={pendingDate}
        onChange={onTextInputChange}
        invalid={invalid}
        {...props}
      />
      <Popover
        open={open}
        onCloseSignal={hideDatepicker}
        anchor={inputRef.current}
        offset={8}
        flipY={false}
      >
        <DeprecatedDatepicker
          selectedDate={selectedDate}
          initialVisibleDate={selectedDate || undefined}
          minDate={minDate}
          maxDate={maxDate}
          endOfDay={endOfDay}
          onChange={d => {
            onChangeWrapper(d);
            hideDatepicker();
          }}
        />
      </Popover>
    </>
  );
};
